import { useCallback, useState } from 'react';
import html2canvas from 'html2canvas';

// 教练战术板导出钩子：
// - 同时下载 PNG 截图 + JSON 结构化数据
// - PNG 用 html2canvas 直接对捕获节点做 DOM 渲染
// - JSON 由调用方通过 buildJson() 现场构造（含 BO 系列、当前局、全局锁）
// 文件名格式：BP-Tactic-YYYYMMDD-HHMMSS.png / .json
const useExportTactic = ({ getCaptureNode, buildJson, filenamePrefix = 'BP-Tactic' }) => {
  const [exporting, setExporting] = useState(false);
  const [lastError, setLastError] = useState(null);

  const exportNow = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    setLastError(null);

    const ts = formatTimestamp();
    const baseName = `${filenamePrefix}-${ts}`;

    try {
      // ===== 1. JSON 总是先生成（即使截图失败也至少能拿到数据） =====
      if (typeof buildJson === 'function') {
        const json = buildJson();
        downloadJson(json, `${baseName}.json`);
      }

      // ===== 2. PNG 截图（DOM → canvas） =====
      const node = typeof getCaptureNode === 'function' ? getCaptureNode() : null;
      if (!node) {
        throw new Error('找不到要截图的节点（getCaptureNode 返回 null）');
      }

      // 关键：截图前先等节点内所有 <img> 加载完，否则会拍到空白格子
      await waitForImages(node, 6000);

      const canvas = await html2canvas(node, {
        backgroundColor: null, // 卡片自己有渐变背景，让 html2canvas 保留透明边
        useCORS: true,
        allowTaint: true,
        scale: 2, // 固定 2x 输出，保证微信群里也清晰
        logging: false,
        // 跳过被打上 data-export-ignore 属性的元素
        ignoreElements: (el) => el.hasAttribute && el.hasAttribute('data-export-ignore'),
      });

      await downloadCanvas(canvas, `${baseName}.png`);
    } catch (e) {
      console.error('[export] failed', e);
      setLastError(e);
      // 把异常以用户友好的方式提示一下
      try {
        // eslint-disable-next-line no-alert
        window.alert(`导出失败：${e.message || e}`);
      } catch (_) {}
    } finally {
      setExporting(false);
    }
  }, [exporting, getCaptureNode, buildJson, filenamePrefix]);

  return { exportNow, exporting, lastError };
};

// ---------- 工具函数 ----------

function formatTimestamp(d = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}` +
    `-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
  );
}

function downloadCanvas(canvas, filename) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        resolve();
        return;
      }
      const url = URL.createObjectURL(blob);
      triggerDownload(url, filename);
      setTimeout(() => {
        URL.revokeObjectURL(url);
        resolve();
      }, 800);
    }, 'image/png');
  });
}

function downloadJson(obj, filename) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], {
    type: 'application/json;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  triggerDownload(url, filename);
  setTimeout(() => URL.revokeObjectURL(url), 800);
}

// 等节点内所有 <img> 加载完成（或失败/超时），保证 html2canvas 不会拍到空白格子。
// timeoutMs：单张图最长等待时间，避免某张图死活加载不出来把整个流程卡死。
function waitForImages(node, timeoutMs = 5000) {
  const imgs = Array.from(node.querySelectorAll('img'));
  if (imgs.length === 0) return Promise.resolve();
  return Promise.all(
    imgs.map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise((resolve) => {
        const done = () => resolve();
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
        setTimeout(done, timeoutMs);
      });
    }),
  );
}

function triggerDownload(href, filename) {
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export default useExportTactic;
